/**
 * Account's plan block (settings pocket spec §6): one statement by billing
 * state, its one action, and for Free the quiet “See what Pro adds” list.
 * “Manage billing” and “Update card” are the only ways into the Stripe
 * portal; Stripe pages open in the system browser (password managers,
 * 3-D Secure) rather than replacing the app.
 */
import { useId, useRef, useState } from "react";
import { DisclosurePanel } from "@/components/ui/disclosure-panel";
import type { ApiError } from "@/api/client";
import {
  createCheckout,
  openBillingPortal,
  startTrial,
  type SubscriptionStatus,
} from "@/api/billing";
import { Button, PocketStatement } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { settingsBillingMessages } from "@/i18n/messages/settings-billing";
import { isTauri } from "@/lib/is-tauri";
import { openExternalUrl } from "@/lib/open-external-url";
import { accountPlan, type AccountPlanAction } from "./account-plan-state";
import { DENKER_BENEFIT_KEYS } from "./billing-plan-content";

function openStripeUrl(url: string): Promise<void> {
  return openExternalUrl(url, { webTarget: "self" });
}

export function AccountPlanBlock({
  status,
  onStatusChange,
}: {
  status: SubscriptionStatus;
  onStatusChange: (status: SubscriptionStatus) => void;
}) {
  const t = useT(settingsBillingMessages);
  const plan = accountPlan(status);
  const action = plan.action;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProAdds, setShowProAdds] = useState(false);
  const benefitsId = useId();
  const benefitsToggleRef = useRef<HTMLButtonElement>(null);

  async function openPortal() {
    setBusy(true);
    try {
      await openStripeUrl(await openBillingPortal());
      if (isTauri()) setBusy(false);
    } catch {
      setError(t("portalFailed"));
      setBusy(false);
    }
  }

  async function checkout() {
    const subscription = status.subscription;
    if (
      subscription &&
      !["canceled", "incomplete_expired"].includes(subscription.status)
    ) {
      await openPortal();
      return;
    }
    const priceId = status.prices.pro_monthly;
    if (!priceId) {
      setError(t("billingNotConfigured"));
      return;
    }
    setBusy(true);
    try {
      const url = await createCheckout(priceId);
      window.dispatchEvent(new Event("billing-checkout-opened"));
      await openStripeUrl(url);
      if (isTauri()) setBusy(false);
    } catch (err) {
      const message = (err as ApiError | null)?.data?.error;
      setError(
        typeof message === "string" && message.trim()
          ? message
          : t("checkoutFailed"),
      );
      setBusy(false);
    }
  }

  async function run(kind: AccountPlanAction) {
    setError(null);
    if (kind === "update-card" || kind === "manage-billing") {
      await openPortal();
      return;
    }
    if (kind === "start-trial") {
      setBusy(true);
      try {
        onStatusChange(await startTrial());
      } catch {
        setError(t("trialStartFailed"));
      } finally {
        setBusy(false);
      }
      return;
    }
    await checkout();
  }

  return (
    <PocketStatement
      size="panel"
      title={plan.statement}
      testId="account-plan"
      line={
        plan.line || plan.extraLine ? (
          <>
            {plan.line ? <p>{plan.line}</p> : null}
            {plan.extraLine ? (
              <p className="mt-0.5 text-appkit-small">{plan.extraLine}</p>
            ) : null}
          </>
        ) : undefined
      }
      action={
        <>
          {action ? (
            <Button
              size="sm"
              variant={action.primary ? "default" : "secondary"}
              disabled={busy}
              onClick={() => void run(action.kind)}
              data-testid="account-plan-action"
            >
              {action.label}
            </Button>
          ) : null}
          {plan.free ? (
            <Button
              size="sm"
              variant="ghost"
              ref={benefitsToggleRef}
              aria-controls={benefitsId}
              aria-expanded={showProAdds}
              onClick={() => setShowProAdds((open) => !open)}
              className="gap-0.5 px-0"
              data-testid="account-pro-adds-toggle"
            >
              {t("seeWhatProAdds")}
              <Icons.ChevronDown
                className="denker-disclosure-chevron h-3 w-3"
                data-open={showProAdds}
                aria-hidden="true"
              />
            </Button>
          ) : null}
          {plan.free ? (
            <div className="basis-full">
              <DisclosurePanel
                id={benefitsId}
                open={showProAdds}
                returnFocusRef={benefitsToggleRef}
              >
                <ul
                  className="flex flex-col gap-1.5 pb-3.5 text-appkit-body text-muted"
                  data-testid="account-pro-adds"
                >
                  {DENKER_BENEFIT_KEYS.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2">
                      <Icons.Check
                        className="h-3.5 w-3.5 shrink-0 text-accent"
                        aria-hidden="true"
                      />
                      {t(benefit)}
                    </li>
                  ))}
                </ul>
              </DisclosurePanel>
              <p
                className="text-appkit-small text-muted"
                data-testid="account-ai-charges-note"
              >
                {t("aiChargesNote")}
              </p>
            </div>
          ) : null}
          {error ? (
            <p
              role="alert"
              className="basis-full text-appkit-small font-medium text-danger"
              data-testid="account-plan-error"
            >
              {error}
            </p>
          ) : null}
        </>
      }
    />
  );
}
